import { Box } from "@chakra-ui/layout";
import { Text, Flex, Button } from "@chakra-ui/react";
import { Row, Col } from "antd";
import { showErrorAxios, showSuccess } from "infra/helpers/alerta";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import DefaultButton from "ui/components/Button/PrimaryButton";
import Field from "../Field";
import OutlineButton from "ui/components/Button/OutlineButton";
import useSaveGrupoUnidade from "core/features/grupo-unidades/hooks/useSaveGrupoUnidade";
import {
  GrupoUnidadeForm,
  ImportarExcelGrupoUnidadeItem,
} from "core/features/grupo-unidades/typings";
import SelectUnidade from "../SelectUnidade";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import InputFile from "components/Input/InputFile";
import TabelaExemploExcelGrupoUnidade from "./TabelaExemploExcelGrupoUnidade";
import excelToJson from "infra/helpers/excelToJson";
import { exportExcelExemploGrupoUnidades } from "core/features/grupo-unidades/helper";
import { MdCloudDownload } from "react-icons/md";

function FormGrupoUnidade({
  onSuccess,
  onClose,
  visible,
  defaultValue,
  editId,
  showCadastroUnidades,
}: {
  onSuccess?: (data: { id: string; nome: string }) => Promise<void>;
  onClose?: () => void;
  visible: boolean;
  defaultValue?: Partial<GrupoUnidadeForm>;
  editId?: string;
  showCadastroUnidades?: boolean;
}) {
  const [numUnidades, setNumUnidades] = useState(0);
  const { tenant } = useCurrentTenant();

  const { control, handleSubmit, reset, formState } = useForm<GrupoUnidadeForm>(
    {
      reValidateMode: "onChange",
      mode: "onChange",
    }
  );

  const { isLoading, mutate: handleSave } = useSaveGrupoUnidade();

  const [isLocalLoading, setIsLocalLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      reset({ ...defaultValue });
      setNumUnidades(0);
    } else {
      reset({});
    }
  }, [visible, defaultValue, reset]);

  const onSubmit = async (data: GrupoUnidadeForm) => {
    const _unidades = Array.from(new Set(data.unidades));

    let unidadesExcel: ImportarExcelGrupoUnidadeItem[] = [];
    if (data.excel) {
      unidadesExcel = await excelToJson<
        ImportarExcelGrupoUnidadeItem & { index: string },
        keyof ImportarExcelGrupoUnidadeItem
      >(data.excel);
    }

    const _data = {
      ...data,
      id: editId || data.id,
      unidades: _unidades,
      unidadesExcel,
    };
    handleSave(_data, {
      onSuccess: async (res) => {
        const successMessage = `Grupo salvo com sucesso.${
          res.data.numeroUnidadesImportadas
            ? `\n${res.data.numeroUnidadesImportadas} ${
                res.data.numeroUnidadesImportadas === 1
                  ? "unidade importada"
                  : "unidades importadas"
              }`
            : ""
        }`;

        showSuccess(successMessage).then(async () => {
          reset();
          setIsLocalLoading(true);
          await onSuccess?.(res.data);
          setIsLocalLoading(false);
        });
      },
      onError: (res) => {
        showErrorAxios(res);
      },
    });
  };

  return (
    <form onSubmit={(ev) => handleSubmit(onSubmit)(ev)}>
      <Text as="h3" fontSize={20} fontWeight="500">
        Cadastrar grupo de unidade
      </Text>
      <Row gutter={showCadastroUnidades ? 16 : 0}>
        <Box as={Col} sm={showCadastroUnidades ? 12 : 24} mb={4}>
          <Field
            label="Nome"
            control={control}
            name="nome"
            rules={{
              required: true,
            }}
          />
        </Box>

        {showCadastroUnidades ? (
          <Box as={Col} sm={12} mb={4}>
            {numUnidades === 0 ? (
              <OutlineButton
                onClick={() => setNumUnidades(1)}
                type="button"
                mt={5}
              >
                Adicionar unidade
              </OutlineButton>
            ) : (
              <Box as={Row} gutter={16}>
                <Box as={Col} sm={20} mb={4}>
                  <Field
                    rules={{
                      required: true,
                    }}
                    label={"Unidade"}
                    control={control}
                    name={`unidades`}
                    render={({ field, isInvalid }) => (
                      <SelectUnidade
                        tenantId={tenant.id}
                        onChange={field.onChange}
                        value={field.value}
                        isInvalid={isInvalid}
                        mode={"multiple"}
                      />
                    )}
                  />
                </Box>
              </Box>
            )}
          </Box>
        ) : null}
      </Row>

      {showCadastroUnidades ? (
        <Box mt={4}>
          <Flex gap={4}>
            <Box>
              <Field
                control={control}
                name="excel"
                render={({ field }) => (
                  <InputFile
                    onChange={field.onChange}
                    label="Importar Excel"
                    name={field.value?.name}
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    value={field.value}
                    showPreview={false}
                  />
                )}
              />
            </Box>
            <Button
              leftIcon={<MdCloudDownload fontSize={16} />}
              iconSpacing={2}
              bg="green.500"
              color="white"
              _hover={{
                bg: "green.400",
              }}
              fontSize={14}
              onClick={exportExcelExemploGrupoUnidades}
            >
              Baixar modelo de Planilha
            </Button>
          </Flex>

          <Box mt={4}>
            <TabelaExemploExcelGrupoUnidade />
          </Box>
        </Box>
      ) : null}

      <Box
        pt="32px"
        justifyContent="flex-end"
        d="flex"
        gridGap="8px"
        flexDir="row"
      >
        <OutlineButton
          onClick={() => {
            onClose?.();
            reset();
          }}
        >
          Cancelar
        </OutlineButton>

        <DefaultButton
          type="button"
          isLoading={isLoading || isLocalLoading}
          onClick={handleSubmit(onSubmit)}
          disabled={!formState.isValid || isLoading || isLocalLoading}
          minWidth={"120px"}
        >
          Salvar
        </DefaultButton>
      </Box>
    </form>
  );
}

export default FormGrupoUnidade;
